'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle, Button, Caption } from '@material-bank/mb3-components';
import { ArrowUpDownIcon, ListFilterIcon } from '@material-bank/mtag-icons';

// The option shown for items whose brand is missing. Exported so the list that offers it
// and the filter that applies it agree on the value — comparing a raw `brandName` against
// it selects nothing, which shows an empty grid for an option the user can pick.
export const UNKNOWN_BRAND = 'Unknown';
export type SortDirection = 'asc' | 'desc';
export type SortMode = 'relevance' | 'dark-to-light' | 'light-to-dark';
export type BrandCount = {
  name: string;
  count: number;
};
type ProductFilterBarProps = {
  brands: BrandCount[];
  selectedBrand: string | null;
  onBrandChange: (brand: string | null) => void;
  filterLabel: string;
  filterByBrandLabel: string;
  allBrandsLabel: string;
  className?: string;
  /** Two-mode lightness sort (used by pairs-with). */
  sortDirection?: SortDirection;
  onSortDirectionChange?: (direction: SortDirection) => void;
  sortAscLabel?: string;
  sortDescLabel?: string;
  /** Three-mode sort cycle (used by similar). Takes precedence when provided. */
  sortMode?: SortMode;
  onSortModeChange?: (mode: SortMode) => void;
  sortModeLabels?: Record<SortMode, string>;
};
function getNextSortMode(mode: SortMode): SortMode {
  if (mode === 'relevance') {
    return 'dark-to-light';
  }
  if (mode === 'dark-to-light') {
    return 'light-to-dark';
  }
  return 'relevance';
}
export function ProductFilterBar(t0) {
  const $ = _c(58);
  const {
    brands,
    selectedBrand,
    onBrandChange,
    filterLabel,
    filterByBrandLabel,
    allBrandsLabel,
    className,
    sortDirection: t1,
    onSortDirectionChange,
    sortAscLabel,
    sortDescLabel,
    sortMode: t2,
    onSortModeChange,
    sortModeLabels
  } = t0;
  const sortDirection = t1 === undefined ? "asc" : t1;
  const sortMode = t2 === undefined ? "relevance" : t2;
  const [drawerOpen, setDrawerOpen] = useState(false);
  let t3;
  if ($[0] !== onSortDirectionChange || $[1] !== sortDirection) {
    t3 = function toggleSortDirection() {
      if (!onSortDirectionChange) {
        return;
      }
      onSortDirectionChange(sortDirection === "asc" ? "desc" : "asc");
    };
    $[0] = onSortDirectionChange;
    $[1] = sortDirection;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const toggleSortDirection = t3;
  let t4;
  if ($[3] !== onSortModeChange || $[4] !== sortMode) {
    t4 = function cycleSortMode() {
      if (!onSortModeChange) {
        return;
      }
      onSortModeChange(getNextSortMode(sortMode));
    };
    $[3] = onSortModeChange;
    $[4] = sortMode;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const cycleSortMode = t4;
  let t5;
  if ($[6] !== onBrandChange) {
    t5 = function selectBrand(brand) {
      onBrandChange(brand);
      setDrawerOpen(false);
    };
    $[6] = onBrandChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const selectBrand = t5;
  const directionLabel = sortDirection === "asc" ? sortAscLabel : sortDescLabel;
  const modeLabel = sortModeLabels ? sortModeLabels[sortMode] : "";
  const t6 = `flex min-w-0 items-center justify-between gap-2 ${className ?? ""}`;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setDrawerOpen(true);
    t8 = <ListFilterIcon size={16} className="shrink-0" />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  const t9 = selectedBrand ?? filterLabel;
  let t10;
  if ($[10] !== t9) {
    t10 = <Caption className="truncate">{t9}</Caption>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== filterLabel || $[13] !== t10) {
    t11 = <Button type="button" size="sm" variant="secondary" className="max-w-[55%] rounded-full" aria-label={filterLabel} onClick={t7}>{t8}{t10}</Button>;
    $[12] = filterLabel;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  let t13;
  if ($[15] !== filterByBrandLabel) {
    t12 = <DrawerTitle className="sr-only">{filterByBrandLabel}</DrawerTitle>;
    t13 = <DrawerDescription className="sr-only">{filterByBrandLabel}</DrawerDescription>;
    $[15] = filterByBrandLabel;
    $[16] = t12;
    $[17] = t13;
  } else {
    t12 = $[16];
    t13 = $[17];
  }
  const t14 = `rounded-lg px-4 py-3 text-left transition-colors ${selectedBrand === null ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`;
  let t15;
  if ($[18] !== selectBrand) {
    t15 = () => selectBrand(null);
    $[18] = selectBrand;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = selectedBrand === null ? "medium" : "regular";
  let t17;
  if ($[20] !== allBrandsLabel || $[21] !== t16) {
    t17 = <Caption weight={t16}>{allBrandsLabel}</Caption>;
    $[20] = allBrandsLabel;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t14 || $[24] !== t15 || $[25] !== t17) {
    t18 = <button type="button" className={t14} onClick={t15}>{t17}</button>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== brands || $[28] !== selectBrand || $[29] !== selectedBrand) {
    let t20;
    if ($[31] !== selectBrand || $[32] !== selectedBrand) {
      t20 = t21 => {
        const {
          name,
          count
        } = t21;
        return <button key={name} type="button" className={`rounded-lg px-4 py-3 text-left transition-colors ${selectedBrand === name ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`} onClick={() => selectBrand(name)}><Caption weight={selectedBrand === name ? "medium" : "regular"}>{name}<span className="ml-2 opacity-60">{count}</span></Caption></button>;
      };
      $[31] = selectBrand;
      $[32] = selectedBrand;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    t19 = brands.map(t20);
    $[27] = brands;
    $[28] = selectBrand;
    $[29] = selectedBrand;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <div className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto px-4 pb-6 pt-4">{t18}{t19}</div>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t20) {
    t21 = <DrawerContent className="rounded-t-[20px]">{t12}{t13}{t20}</DrawerContent>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== drawerOpen || $[42] !== t11 || $[43] !== t21) {
    t22 = <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>{t11}{t21}</Drawer>;
    $[41] = drawerOpen;
    $[42] = t11;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== cycleSortMode || $[46] !== directionLabel || $[47] !== modeLabel || $[48] !== onSortDirectionChange || $[49] !== onSortModeChange || $[50] !== toggleSortDirection) {
    t23 = onSortModeChange ? <Button type="button" size="sm" variant="secondary" className="shrink-0 rounded-full" aria-label={modeLabel} onClick={cycleSortMode}><ArrowUpDownIcon size={16} className="shrink-0" /><Caption className="whitespace-nowrap">{modeLabel}</Caption></Button> : onSortDirectionChange ? <Button type="button" size="sm" variant="secondary" className="shrink-0 rounded-full" aria-label={directionLabel} onClick={toggleSortDirection}><ArrowUpDownIcon size={16} className="shrink-0" /><Caption className="whitespace-nowrap">{directionLabel}</Caption></Button> : null;
    $[45] = cycleSortMode;
    $[46] = directionLabel;
    $[47] = modeLabel;
    $[48] = onSortDirectionChange;
    $[49] = onSortModeChange;
    $[50] = toggleSortDirection;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t23) {
    t24 = <div className="flex min-w-0 items-center justify-end gap-2">{t23}</div>;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t22 || $[55] !== t24 || $[56] !== t6) {
    t25 = <div className={t6}>{t22}{t24}</div>;
    $[54] = t22;
    $[55] = t24;
    $[56] = t6;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  return t25;
}